import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchProductInfo, askProductQuestion } from '@/services/productInfo';

// "Learn more" product modal — the AI-powered About/Common-uses description plus
// a live Q&A chat, matching the patient-dashboard shop's ProductInfoModal.
// Rendered over the intake flow; because the patient is already inside the
// intake we suppress the dashboard's "Start your visit" CTA and close with a
// plain "Back to your visit" button (PRD context="intake").
//
// Portal to <body>: the slide-wrapper keeps a non-`none` CSS transform after its
// enter animation, which would trap this position:fixed overlay to the wrapper
// (clipping + broken scroll). document.body has no transformed ancestor. Same
// pattern as CatalogCheckoutStep's "Modify your plan" modal.

const BRAND = '#2d8a5f';

// Perplexity returns light Markdown (**bold**). Render it inline without a
// dependency: split on ** and wrap odd segments in <strong>.
const renderInline = (text) =>
  String(text)
    .split(/\*\*/)
    .map((seg, i) => (i % 2 === 1 ? <strong key={i}>{seg}</strong> : seg));

const Paragraphs = ({ text }) =>
  String(text)
    .split(/\n{2,}/)
    .filter((p) => p.trim())
    .map((p, i) => (
      <p key={i} className={i > 0 ? 'mt-3' : ''}>
        {renderInline(p)}
      </p>
    ));

const ProductInfoModal = ({ product, open, onClose }) => {
  const medId = product?.medId;
  const name = product?.name || 'this treatment';
  const subtitle = product?.dose || product?.strength || '';

  const [loadingInfo, setLoadingInfo] = useState(true);
  const [info, setInfo] = useState({ description: '', commonUses: [] });
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', content }
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  // Load the About/Common-uses content when the modal opens.
  useEffect(() => {
    if (!open || !medId) return;
    const controller = new AbortController();
    setLoadingInfo(true);
    setInfo({ description: '', commonUses: [] });
    setMessages([]);
    setDraft('');
    fetchProductInfo(
      { medId, name: product?.name, visitType: product?.visitType },
      controller.signal
    )
      .then((res) => setInfo(res))
      .catch(() => {})
      .finally(() => setLoadingInfo(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, medId]);

  // Keep the newest chat message in view.
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, sending]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !medId) return null;

  const send = async () => {
    const question = draft.trim();
    if (!question || sending) return;
    const history = messages.slice();
    setMessages((m) => [...m, { role: 'user', content: question }]);
    setDraft('');
    setSending(true);
    let answer = '';
    try {
      answer = await askProductQuestion({
        medId,
        name: product?.name,
        question,
        history,
      });
    } catch {
      answer = '';
    }
    setMessages((m) => [
      ...m,
      {
        role: 'assistant',
        content:
          answer ||
          "Product Q&A isn't available right now. Your provider can answer this during your visit.",
      },
    ]);
    setSending(false);
  };

  const onDraftKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const hasDescription = !loadingInfo && info.description;
  const hasUses = !loadingInfo && info.commonUses.length > 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      data-testid="product-info-modal"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`About ${name}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div className="min-w-0 pr-3">
            <h3 className="font-semibold text-gray-900 leading-snug">{name}</h3>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-2 flex-shrink-0"
          >
            ×
          </button>
        </div>

        {/* Scrollable body: About + Common uses + chat transcript */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-5 space-y-5">
          {/* About */}
          <div>
            <h4 className="flex items-center gap-2 font-medium text-gray-900 mb-2">
              <span aria-hidden="true">✨</span> About {name}
            </h4>
            {loadingInfo ? (
              <div className="space-y-2" aria-hidden="true">
                <div className="h-3 rounded bg-gray-100 w-full" />
                <div className="h-3 rounded bg-gray-100 w-11/12" />
                <div className="h-3 rounded bg-gray-100 w-4/5" />
              </div>
            ) : hasDescription ? (
              <div className="text-sm text-gray-700 leading-relaxed">
                <Paragraphs text={info.description} />
              </div>
            ) : (
              <p className="text-sm text-gray-500" data-testid="product-info-degraded">
                A detailed description for {name} is coming soon. Your provider
                can walk you through it during your visit.
              </p>
            )}
          </div>

          {/* Common uses */}
          {hasUses && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Common uses</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                {info.commonUses.map((u, i) => (
                  <li key={i}>{renderInline(u)}</li>
                ))}
              </ul>
            </div>
          )}

          {(hasDescription || hasUses) && (
            <p className="text-xs text-gray-400">
              {info.disclaimer ||
                'Educational information only — not medical advice. A clinician confirms suitability.'}
            </p>
          )}

          {/* Chat */}
          <div className="pt-1 border-t border-gray-100">
            <h4 className="flex items-center gap-2 font-medium text-gray-900 mt-4 mb-3">
              <span aria-hidden="true">💬</span> Ask about {name}
            </h4>
            <div className="space-y-2" data-testid="product-chat-transcript">
              {messages.length === 0 && !sending && (
                <p className="text-sm text-gray-400">
                  Ask a question below — e.g. “How is this taken?” or “What are
                  common side effects?”
                </p>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-[#2d8a5f] text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {m.role === 'assistant' ? renderInline(m.content) : m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-500 rounded-2xl px-3.5 py-2 text-sm">
                    Thinking…
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </div>
        </div>

        {/* Composer + close */}
        <div className="border-t border-gray-100 p-4 flex-shrink-0 space-y-3">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onDraftKeyDown}
              rows={1}
              placeholder={`Ask a question about ${name}…`}
              data-testid="product-chat-input"
              className="flex-1 resize-none rounded-xl border border-gray-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d8a5f]/40 focus:border-[#2d8a5f] max-h-28"
            />
            <button
              type="button"
              onClick={send}
              disabled={!draft.trim() || sending}
              aria-label="Send question"
              data-testid="product-chat-send"
              className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#2d8a5f] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#246f4d] transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l14-7-7 14-2-5-5-2z" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="product-info-back"
            className="w-full rounded-xl border border-gray-300 px-4 py-2.5 font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Back to your visit
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export { BRAND };
export default ProductInfoModal;
